import { nativeOnChain, WRAPPED_NATIVE_CURRENCY } from 'constants/tokens'
import { BigintIsh, CurrencyAmount, Token, TradeType, WETH9 } from 'eth-mainnet-few-sdk-core-2'
// This file is lazy-loaded, so the import of smart-order-router is intentional.
// eslint-disable-next-line no-restricted-imports
import { AlphaRouter, AlphaRouterConfig } from 'eth-mainnet-smart-order-router-4'
import { getWrappedTokenCreate2Address } from 'hooks/Tokens'
import JSBI from 'jsbi'
import { GetQuoteArgs, QuoteResult, QuoteState, SwapRouterNativeAssets } from 'state/routing/types'
import { transformSwapRouteToGetQuoteResult } from 'utils/transformSwapRouteToGetQuoteResult'

async function getQuote(
  {
    tradeType,
    tokenIn,
    tokenOut,
    amount: amountRaw,
  }: {
    tradeType: TradeType
    tokenIn: { address: string; chainId: number; decimals: number; symbol?: string }
    tokenOut: { address: string; chainId: number; decimals: number; symbol?: string }
    amount: BigintIsh
  },
  router: AlphaRouter,
  routerConfig: Partial<AlphaRouterConfig>
): Promise<QuoteResult> {
  const tokenInIsNative = Object.values(SwapRouterNativeAssets).includes(tokenIn.address as SwapRouterNativeAssets)
  const tokenOutIsNative = Object.values(SwapRouterNativeAssets).includes(tokenOut.address as SwapRouterNativeAssets)

  const currencyIn = tokenInIsNative
    ? nativeOnChain(tokenIn.chainId)
    : new Token(tokenIn.chainId, tokenIn.address, tokenIn.decimals, tokenIn.symbol)
  const currencyOut = tokenOutIsNative
    ? nativeOnChain(tokenOut.chainId)
    : new Token(tokenOut.chainId, tokenOut.address, tokenOut.decimals, tokenOut.symbol)

  const baseCurrency = tradeType === TradeType.EXACT_INPUT ? currencyIn : currencyOut
  const quoteCurrency = tradeType === TradeType.EXACT_INPUT ? currencyOut : currencyIn

  const amount = CurrencyAmount.fromRawAmount(baseCurrency, JSBI.BigInt(amountRaw))
  // TODO (WEB-2055): explore initializing client side routing on first load (when amountRaw is null) if there are enough users using client-side router preference.
  const swapRoute = await router.route(amount, quoteCurrency, tradeType, /*swapConfig=*/ undefined, routerConfig)

  if (!swapRoute) {
    return { state: QuoteState.NOT_FOUND }
  }

  return transformSwapRouteToGetQuoteResult(tradeType, amount, swapRoute)
}

export async function getClientSideQuote(
  {
    tokenInAddress,
    tokenInChainId,
    tokenInDecimals,
    tokenInSymbol,
    tokenOutAddress,
    tokenOutChainId,
    tokenOutDecimals,
    tokenOutSymbol,
    amount,
    tradeType,
  }: GetQuoteArgs,
  router: AlphaRouter,
  config: Partial<AlphaRouterConfig>
) {
  const fewTokenInAddress =
    tokenInAddress === 'ETH'
      ? getWrappedTokenCreate2Address(
          WRAPPED_NATIVE_CURRENCY[tokenInChainId]?.address ?? WETH9[tokenInChainId].address,
          tokenInChainId
        )
      : getWrappedTokenCreate2Address(tokenInAddress, tokenInChainId)

  const fewTokenOutAddress =
    tokenOutAddress === 'ETH'
      ? getWrappedTokenCreate2Address(
          WRAPPED_NATIVE_CURRENCY[tokenInChainId]?.address ?? WETH9[tokenInChainId].address,
          tokenInChainId
        )
      : getWrappedTokenCreate2Address(tokenOutAddress, tokenInChainId)

  return getQuote(
    {
      tradeType,
      tokenIn: {
        address: fewTokenInAddress,
        chainId: tokenInChainId,
        decimals: tokenInDecimals,
        symbol: 'fw' + tokenInSymbol,
      },
      tokenOut: {
        address: fewTokenOutAddress,
        chainId: tokenOutChainId,
        decimals: tokenOutDecimals,
        symbol: 'fw' + tokenOutSymbol,
      },
      amount,
    },
    router,
    config
  )
}
